import type { ReactNode } from 'react';
import clsx from 'clsx';

type AdminButtonVariant = 'primary' | 'secondary' | 'danger';
type AdminButtonSize = 'sm' | 'md';

const variantStyles: Record<AdminButtonVariant, { base: string; hover: string; disabled: string }> =
  {
    primary: {
      base: 'bg-[#4188ff] text-white',
      hover: 'hover:bg-[#154fb2]',
      disabled: 'disabled:bg-[#8bb6ff]',
    },
    secondary: {
      base: 'bg-[#f5f5f5] text-[#333]',
      hover: 'hover:bg-[#ccc]',
      disabled: 'disabled:text-[#ccc]',
    },
    danger: {
      base: 'bg-[#ff4e4e] text-white',
      hover: 'hover:bg-[#d12525]',
      disabled: 'disabled:bg-[#ffa2a2]',
    },
  };

const sizeStyles: Record<AdminButtonSize, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'px-4 py-3 text-sm',
};

type AdminButtonProps = {
  label: string;
  variant?: AdminButtonVariant;
  size?: AdminButtonSize;
  icon?: ReactNode;
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onClick?: () => void;
};

export function AdminButton({
  label,
  variant = 'primary',
  size = 'md',
  icon,
  className,
  disabled,
  type = 'button',
  onClick,
}: AdminButtonProps) {
  const styles = variantStyles[variant];
  return (
    <button
      type={type}
      className={clsx(
        'inline-flex cursor-pointer items-center justify-center gap-2 rounded-full border-0 font-bold whitespace-nowrap transition',
        sizeStyles[size],
        styles.base,
        styles.hover,
        'disabled:cursor-not-allowed',
        styles.disabled,
        className
      )}
      disabled={disabled}
      onClick={onClick}
    >
      {icon}
      {label}
    </button>
  );
}

type AdminIconButtonProps = {
  label: string;
  icon: ReactNode;
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onClick?: () => void;
};

export function AdminIconButton({
  label,
  icon,
  className,
  disabled,
  type = 'button',
  onClick,
}: AdminIconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      className={clsx(
        'inline-flex size-5 cursor-pointer items-center justify-center rounded border border-solid border-border bg-white text-main',
        'disabled:cursor-not-allowed disabled:opacity-60',
        className
      )}
      disabled={disabled}
      onClick={onClick}
    >
      {icon}
    </button>
  );
}
